import { formatDateTimeInZone, halifaxParts, partsInTimeZone } from "./clock";
import { parseCharterInstant } from "./simbrief";

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTH_FULL =
  "January|February|March|April|May|June|July|August|September|October|November|December";
const MONTH_SHORT = "Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sept|Sep|Oct|Nov|Dec";
const MONTH_ANY = `(?:${MONTH_FULL}|${MONTH_SHORT})`;
/** Day number that is not the first half of a 4-digit year (avoids "SEP 2026"). */
const DAY_NUM = String.raw`(?<!\d)\d{1,2}(?!\d)`;

/** STD-shaped keys. Email Date / createdAt are intentionally absent. */
const STD_KEYS = [
  "departureIso",
  "departureLocal",
  "std",
  "stdIso",
  "scheduledDeparture",
  "scheduledTimeOfDeparture",
  "etd",
  "etdIso",
  "departure",
] as const;

export function halifaxOrdinalDay(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** Human charter-on line in America/Halifax (e.g. "charter on 20th of September"). */
export function formatCharterOnDate(date: Date): string {
  const p = halifaxParts(date);
  const day = Number(p.day);
  const month = MONTHS_LONG[Number(p.month) - 1] ?? p.month;
  return `charter on ${halifaxOrdinalDay(day)} of ${month}`;
}

/** Same Halifax calendar day as the ticket STD, or null when STD is missing/invalid. */
export function charterOnLabel(departureIso: string | Date | number | null | undefined): string | null {
  const instant = parseCharterInstant(departureIso);
  if (!instant) return null;
  return formatCharterOnDate(instant);
}

export function pickStdRaw(row: Record<string, unknown>): unknown {
  for (const key of STD_KEYS) {
    const value = row[key];
    if (value != null && value !== "") return value;
  }
  return undefined;
}

/**
 * Parse an Inbox departure. STD-shaped fields win. Naive ISO is America/Halifax
 * wall time. Past / unparseable values fall back to `fallback` (usually suggestDeparture).
 */
export function parseInboxDeparture(raw: unknown, now: Date, fallback: Date): Date {
  const instant = parseCharterInstant(
    raw instanceof Date || typeof raw === "string" || typeof raw === "number" ? raw : null,
  );
  if (instant && instant.getTime() >= now.getTime() - 3_600_000) return instant;
  return fallback;
}

function charterOnPhraseRe(): RegExp {
  const year = String.raw`(?:,?\s*\d{4})?`;
  const dayFirstOrdinal = String.raw`${DAY_NUM}(?:st|nd|rd|th)\s+(?:of\s+)?${MONTH_ANY}${year}`;
  const monthFirst = String.raw`(?<!\d{1,2}\s)${MONTH_ANY}\s+${DAY_NUM}(?:st|nd|rd|th)?${year}`;
  const charterOn = String.raw`charter\s+on\s+(?:${DAY_NUM}(?:st|nd|rd|th)?\s+(?:of\s+)?${MONTH_ANY}|${MONTH_ANY}\s+${DAY_NUM}(?:st|nd|rd|th)?)${year}`;
  return new RegExp(`${charterOn}|${dayFirstOrdinal}|${monthFirst}`, "gi");
}

/**
 * Rewrite a stale "charter on 8th of September" (or similar) so the visible
 * date matches the ticket STD's Halifax calendar day.
 */
export function alignCharterOnCopy(text: string, departure: Date): string {
  if (!text) return text;
  const label = formatCharterOnDate(departure);
  const dateOnly = label.slice("charter on ".length);
  return text.replace(charterOnPhraseRe(), (match) => {
    if (/charter\s+on/i.test(match)) {
      return match[0] === "C" ? `Charter on ${dateOnly}` : label;
    }
    return dateOnly;
  });
}

/** Desk email "When" / "Requested STD" line in the desk zone. UTC when the desk has no zone. */
export function formatInboxStdLine(date: Date, timeZone = "UTC"): string {
  return formatDateTimeInZone(date, timeZone || "UTC");
}

/**
 * Re-date request copy after the desk moved the STD: the charter-on day, the
 * desk "When:" / "Requested STD:" lines, and a departure clock time
 * ("wheels up around 10:00 local") all follow the filed STD. Other times are left alone.
 */
export function alignRequestStdCopy(text: string, departure: Date, timeZone = "UTC"): string {
  if (!text) return text;
  const zone = timeZone.trim() || "UTC";
  const p = partsInTimeZone(departure, zone);
  const clock = `${p.hour}:${p.minute}`;
  return alignCharterOnCopy(text, departure)
    .replace(/^(When|Requested STD):[^\n]*$/gm, (_, key: string) => `${key}: ${formatInboxStdLine(departure, zone)}`)
    .replace(
      /\b((?:wheels[\s-]up|depart(?:ure|ing)?|leav(?:e|ing)|off(?:\s+the\s+ground)?)\s+(?:(?:around|at|about|by|for)\s+)?)([01]?\d|2[0-3]):[0-5]\d\b/gi,
      (_, lead: string) => `${lead}${clock}`,
    );
}
